import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { lockInput, unlockInput } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { showToast } from './Toast.js';
import { messageFor } from '../api/errors.js';
import { upgradeTableRows } from './backyardUpgradeRows.js';
import { ensureLoaded, loadCtx, prioritiseFor } from './sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';
import { pinToCamera } from './pin.mjs';

const W = 760;
const ROW_H = 58;
const HEADER_H = 96;
const FOOTER_H = 30;

// Table of the five backyard levels — what each gives, its cost, and a buy button on the
// next one. Opens even when you can't afford it, so a kid can read what she's saving for.
export function showBackyardUpgradeModal(scene) {
  return new Promise((resolve) => {
    const rows = upgradeTableRows({
      level: gameState.raw?.backyard?.level ?? 1,
      money: gameState.raw?.money ?? 0,
      levels: gameState.raw?.catalog?.backyard_levels,
      costs: gameState.raw?.catalog?.backyard_upgrades,
    });
    const H = HEADER_H + rows.length * ROW_H + FOOTER_H;
    const x = GAME_WIDTH / 2, y = GAME_HEIGHT / 2;
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);

    const overlay = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xffffff, 1).setStrokeStyle(4, 0x333333);
    const title = iconText(scene, x, y - H / 2 + 20, '{yard} Podwórko — ulepszenia', {
      fontFamily: 'system-ui', fontSize: '26px', color: '#000', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    const sub = iconText(scene, x, y - H / 2 + 58, `Masz: ${gameState.raw?.money ?? 0} {coin}`, {
      fontFamily: 'system-ui', fontSize: '18px', color: '#444',
    }).setOrigin(0.5, 0);
    layer.add([overlay, box, title, sub]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay, fixed: true });   // pinned below: screen space

    const closeBtn = scene.add.rectangle(x + W / 2 - 24, y - H / 2 + 24, 36, 36, 0xaa3333, 1)
      .setStrokeStyle(2, 0x000000).setInteractive({ useHandCursor: true });
    const closeTxt = iconText(scene, x + W / 2 - 24, y - H / 2 + 24, '{cross}', {
      fontFamily: 'system-ui', fontSize: '20px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([closeBtn, closeTxt]);

    const close = () => { audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); resolve(); };
    closeBtn.on('pointerup', close);
    overlay.on('pointerup', close);

    const left = x - W / 2 + 24;
    const firstRowY = y - H / 2 + HEADER_H + ROW_H / 2;
    rows.forEach((r, i) => {
      const ry = firstRowY + i * ROW_H;
      const isNext = r.status === 'next';
      const fill = r.status === 'have' ? 0xdff5dd : isNext ? 0xfff3c4 : 0xeeeeee;
      const band = scene.add.rectangle(x, ry, W - 32, ROW_H - 8, fill, 1).setStrokeStyle(isNext ? 3 : 1, isNext ? 0xe0a800 : 0xbbbbbb);
      const color = r.status === 'locked' ? '#888' : '#000';
      const lvlTxt = iconText(scene, left, ry, `${r.level}`, {
        fontFamily: 'system-ui', fontSize: '24px', color, fontStyle: 'bold',
      }).setOrigin(0, 0.5);
      const what = iconText(scene, left + 44, ry, r.text, {
        fontFamily: 'system-ui', fontSize: '18px', color, wordWrap: { width: 420 },
      }).setOrigin(0, 0.5);
      layer.add([band, lvlTxt, what]);

      if (r.status === 'have') {
        layer.add(iconText(scene, x + W / 2 - 40, ry, '{check} masz', {
          fontFamily: 'system-ui', fontSize: '18px', color: '#2a7a2a', fontStyle: 'bold',
        }).setOrigin(1, 0.5));
        return;
      }
      if (r.status === 'locked') {
        layer.add(iconText(scene, x + W / 2 - 40, ry, r.cost != null ? `${r.cost} {coin}` : '', {
          fontFamily: 'system-ui', fontSize: '18px', color: '#888',
        }).setOrigin(1, 0.5));
        return;
      }
      // next level — buy button (disabled + hint when short of money)
      const canBuy = r.shortBy === 0 && r.cost != null;
      const bx = x + W / 2 - 118, bw = 196, bh = 40;
      const btn = scene.add.rectangle(bx, ry, bw, bh, canBuy ? 0xffd166 : 0xcccccc).setStrokeStyle(2, 0x333333);
      const btnTxt = iconText(scene, bx, ry, `Ulepsz za ${r.cost ?? '?'} {coin}`, {
        fontFamily: 'system-ui', fontSize: '17px', color: canBuy ? '#000' : '#777', fontStyle: 'bold',
      }).setOrigin(0.5);
      layer.add([btn, btnTxt]);
      if (!canBuy) {
        layer.add(iconText(scene, bx, ry + 24, `Brakuje ${r.shortBy} {coin}`, {
          fontFamily: 'system-ui', fontSize: '13px', color: '#b00020',
        }).setOrigin(0.5, 0.5));
        return;
      }
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerup', async () => {
        btn.disableInteractive();
        try {
          // Same ordering issue as MainScene's house upgrade: BackyardScene's
          // gameState.on(() => this._applyState()) listener redraws the background
          // synchronously inside gameState._apply(), before `await
          // gameState.upgradeBackyard()` unblocks below — so the new level's art has to
          // be cached BEFORE the call, against a projected next-level state, or the
          // redraw falls back to the old level's texture.
          const b = gameState.raw?.backyard;
          const nextCtx = loadCtx({ state: { ...gameState.raw, backyard: { ...b, level: (b?.level ?? 1) + 1 } } });
          await ensureLoaded(scene, needsFor(scene.scene.key, nextCtx));
          await gameState.upgradeBackyard();
          prioritiseFor(scene.scene.key);   // …and its other phases next
          showToast(scene, `{party} Podwórko poziom ${gameState.raw?.backyard?.level}!`);
        } catch (e) {
          showToast(scene, messageFor(e));
        }
        close();
      });
    });
    // Podwórko scrolls since 2026-10-22: the table stays on screen (and clickable) wherever the view is.
    pinToCamera(layer);
  });
}
